import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../../../services/HttpManager";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { RequestPromotionDTO, RequestPromotionItemDTO, RequestPromotionLinkDTO } from "../../../../models/EnrollmentHistoryDTO";
import TiraDeMeses from "../../../../components/acuerdos/tiraDeMeses";
import { desfase, CuentaRegresiva, CupoTexto } from "../../../../components/acuerdos/cuentaRegresiva";
import { errorText } from "./helpers";

/*
	Paso "Promoción" de la solicitud de matrícula (Etapa 3 de cobros, decisiones 18-22 de Esteban). Obligatorio: cada curso
	lleva su promoción del catálogo del plantel o «Ninguna» explícita. Lo que se ve aquí es lo que el servidor calcula
	(elegibilidad en vivo, tira de meses sobre precio de lista, "¿con quién?"); al aprobar lo vuelve a revisar todo y, si no
	cuadra, no aprueba. El adelanto 20 %/15 % y los kits regalados son promociones de Caja: no van aquí.
*/

export interface PasoPromocionProps {
	campusId: number;
	requestId: number | null;
	studentId: number | null;
	studentName: string;
	scheduleIds: number[];
	value: RequestPromotionDTO;
	onChange: (v: RequestPromotionDTO) => void;
	busy: boolean;
	marcar: boolean;
	/** Las otras personas que se matriculan junto con esta. */
	bundle: { id: number; name: string; statusId: number; status: string; schedules: string[] }[];
	/** Guarda esta solicitud y abre una nueva para otra persona que se matricula junto con ella. */
	onAddPerson?: () => void;
	/** Abre otra solicitud del grupo. */
	onOpenRequest?: (id: number) => void;
	onLeaveBundle?: () => void;
	/** Lo que el servidor dice que falta (para el aviso del paso y para Revisar). */
	onIssues?: (issues: string[]) => void;
	/** Matrícula directa (Horarios / perfil / alta, A1): sin el bloque de personas que se aprueban juntas. */
	directa?: boolean;
	/** Diplomado por bloques que se vende en cada horario de arranque: { scheduleId: courseId } (promos y precio del diplomado). */
	diplomados?: Record<number, number>;
	/** Corregir Promoción: la matrícula que se va a rehacer no cuenta (ni su promo equivocada, ni para «nuevo»). */
	excluirMatricula?: number | null;
}

const http = HttpManager.getInstance();
const num = (n: number) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("es-MX", { maximumFractionDigits: 2 });
/** Precio de la inscripción con una promo de inscripción (percent o fixed_price, como CobrosService::discountFrom). */
const conPromoInsc = (lista: number, p: any): number => {
	if (!p || lista <= 0.009) return Math.max(0, lista);
	if (p.calcType === "percent") return Math.round(lista * (1 - Math.min(100, Math.max(0, Number(p.value) || 0)) / 100) * 100) / 100;
	if (p.calcType === "fixed_price") return Math.min(lista, Math.max(0, Number(p.value) || 0));
	return lista;
};
const fecha = (d: string | null | undefined) => d ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : "";
const LINK_TEXT: Record<string, string> = {
	segundo: "¿Con quién va el 50 %? (la otra persona o el otro curso)",
	grupo: "¿Con qué grupo?",
	dos_x_uno: "¿Cuál es el curso base que se paga completo?",
	referido_por: "¿Quién lo refirió? (alumno o graduado)"
};

const PasoPromocion: React.FC<PasoPromocionProps> = ({ campusId, requestId, studentId, studentName, scheduleIds, value, onChange, busy, marcar, bundle, onAddPerson, onOpenRequest, onLeaveBundle, onIssues, directa, diplomados, excluirMatricula }) => {
	const [opts, setOpts] = useState<any>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [buscar, setBuscar] = useState<{ sid: number | "ref"; q: string; kind: "matricula" | "grupo" | "alumno" } | null>(null);
	const [resultados, setResultados] = useState<any[] | null>(null);
	const [elegidos, setElegidos] = useState<Record<string, any>>({});
	const timer = useRef<any>(null);
	const timerB = useRef<any>(null);
	// Promos flexibles: al vencer la primera promo con tiempo limitado se vuelven a pedir las opciones (desaparece sola).
	const [tick, setTick] = useState<number>(0);

	const itemOf = (sid: number): RequestPromotionItemDTO => value.items.find((i) => i.scheduleId === sid) || { scheduleId: sid, promotionId: null, inscriptionPromotionId: null, link: null };
	const setItem = (sid: number, patch: Partial<RequestPromotionItemDTO>) => {
		const items = scheduleIds.map((id) => id === sid ? { ...itemOf(id), ...patch } : itemOf(id));
		onChange({ ...value, items });
	};

	// Opciones en vivo: cambian con los cursos, el alumno y lo elegido (lo del vínculo también se valida aquí).
	const firma = JSON.stringify({ scheduleIds, studentId, value, requestId, diplomados, excluirMatricula });
	useEffect(() => {
		clearTimeout(timer.current);
		timer.current = setTimeout(() => {
			setLoading(true);
			http.enrollmentRequestsService.promotionOptions(campusId, { requestId, studentId, schedules: scheduleIds, promotion: value, name: studentName, diplomados, excludeEnrollmentId: excluirMatricula || null })
				.then((r) => { setOpts(r.data); setError(null); if (onIssues) onIssues(r.data.issues || []); })
				.catch((e) => setError(errorText(e)))
				.finally(() => setLoading(false));
		}, 350);
		return () => clearTimeout(timer.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [firma, tick]);
	// Al terminar la primera cuenta regresiva se vuelven a pedir las opciones (la promo vencida sale sola). Se mide con la hora del
	// SERVIDOR (un teléfono adelantado pedía cada segundo hasta que el servidor llegaba a esa hora) y, si tras pedirlas la misma
	// promo sigue con el mismo fin, se espera más antes de volver a preguntar.
	const ultimoFin = useRef<number>(0);
	useEffect(() => {
		const fines: number[] = [];
		let servidor: string | null = null;
		(opts?.schedules || []).forEach((s: any) => (s.promotions || []).forEach((p: any) => {
			const f = Date.parse(p.window?.countdownToIso || "");
			if (isFinite(f)) fines.push(f);
			servidor = servidor || p.window?.serverNowIso || null;
		}));
		if (!fines.length) return;
		const fin = Math.min(...fines);
		let espera = Math.max(1000, fin - (Date.now() + desfase(servidor)) + 1500);
		if (fin === ultimoFin.current) espera = Math.max(espera, 30000);
		if (espera > 2147000000) return;
		const t = setTimeout(() => { ultimoFin.current = fin; setTick((x) => x + 1); }, espera);
		return () => clearTimeout(t);
	}, [opts]);

	const doSearch = (q: string, kind: "matricula" | "grupo" | "alumno") => {
		clearTimeout(timerB.current);
		if (q.trim().length < 2) { setResultados(null); return; }
		timerB.current = setTimeout(() => {
			const call = kind === "alumno" ? http.enrollmentRequestsService.findStudents(campusId, q.trim()) : http.enrollmentRequestsService.linkCandidates(campusId, q.trim());
			call.then((r: any) => setResultados(r.data || [])).catch(() => setResultados([]));
		}, 350);
	};

	const sym = opts?.currencySymbol || "";
	const linkSel = (sid: number, link: RequestPromotionLinkDTO | null, label?: string) => {
		setItem(sid, { link });
		if (label) setElegidos((e) => ({ ...e, [sid + ":" + (link?.type || "")]: label }));
		setBuscar(null); setResultados(null);
	};

	const buscador = (sid: number | "ref", kind: "matricula" | "grupo" | "alumno", placeholder: string, onPick: (r: any) => void) => (
		<div style={{ marginTop: 6, maxWidth: 560 }}>
			<input className="form-control form-control-sm" autoFocus placeholder={placeholder} value={buscar && buscar.sid === sid && buscar.kind === kind ? buscar.q : ""} disabled={busy}
				onChange={(e) => { setBuscar({ sid, q: e.target.value, kind }); doSearch(e.target.value, kind); }} />
			{buscar && buscar.sid === sid && buscar.kind === kind && resultados !== null ? (
				<div className="waResultados" style={{ maxHeight: 240, overflow: "auto" }}>
					{resultados.length < 1 ? <div style={{ padding: 8, color: "var(--wa-apagado)" }}>Sin resultados</div> : resultados
						.filter((r: any) => kind !== "grupo" || r.group)
						.map((r: any) => (
							<button type="button" key={"r" + (r.enrollmentId || r.id)} className="waResultado" onClick={() => onPick(r)}>
								<b>{r.name}</b>
								<span style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
									{kind === "alumno" ? [r.identityCode, r.phone1].filter(Boolean).join(" · ")
										: [cursoCorto(r.courseName), r.campusName, r.enrolledAt ? "matriculado " + fecha(r.enrolledAt) + (r.daysAgo > 10 ? " (hace " + r.daysAgo + " días)" : "") : "", r.agreement ? "promo: " + r.agreement : "sin promo"].filter(Boolean).join(" · ")}
									{r.group ? <><br />{r.group.kindName}: {r.group.members.join(", ")}</> : null}
								</span>
							</button>
						))}
					{kind === "grupo" && resultados.length > 0 && !resultados.some((r: any) => r.group) ? <div style={{ padding: 8, color: "var(--wa-apagado)" }}>Ninguna de esas matrículas está en un grupo.</div> : null}
				</div>
			) : null}
		</div>
	);

	const vinculo = (sid: number, p: any) => {
		const it = itemOf(sid);
		const link = it.link || null;
		const otros = (opts?.bundleItems || []).filter((b: any) => !(b.isThis && b.scheduleId === sid));
		const elegido = (t: string) => elegidos[sid + ":" + t];
		const grupoPrev = (opts?.regroup || []).find((g: any) => g.scheduleId === sid);
		return (
			<div className="waTarjeta" style={{ marginTop: 8, background: "var(--wa-papel)" }}>
				<div style={{ fontWeight: 700, marginBottom: 6 }}>{LINK_TEXT[p.linkType] || "¿Con quién?"}</div>
				{(p.linkType === "segundo" || p.linkType === "dos_x_uno") ? (
					<>
						{otros.length > 0 ? <div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>En esta matrícula:</div> : null}
						{otros.map((b: any) => (
							<label key={b.key} style={{ position: "static", display: "flex", gap: 8, alignItems: "center", margin: "4px 0", fontSize: 15, whiteSpace: "normal", overflowWrap: "anywhere" }}>
								<input type="radio" checked={link?.type === "solicitud" && link.requestId === b.requestId && link.scheduleId === b.scheduleId} disabled={busy}
									onChange={() => linkSel(sid, { type: "solicitud", requestId: b.requestId, scheduleId: b.scheduleId })} />
								<span><b>{b.isThis ? "Su Otro Curso" : b.name}</b> · {cursoCorto(b.courseName)}</span>
							</label>
						))}
						<label style={{ position: "static", display: "flex", gap: 8, alignItems: "center", margin: "4px 0", fontSize: 15, whiteSpace: "normal", overflowWrap: "anywhere" }}>
							<input type="radio" checked={link?.type === "matricula" || (!!buscar && buscar.sid === sid && buscar.kind === "matricula")} disabled={busy} onChange={() => setBuscar({ sid, q: "", kind: "matricula" })} />
							<span>Un alumno ya inscrito (o su otro curso activo){link?.type === "matricula" && elegido("matricula") ? <b>: {elegido("matricula")}</b> : null}</span>
						</label>
						{buscar && buscar.sid === sid && buscar.kind === "matricula" ? buscador(sid, "matricula", "Busca por nombre, correlativo o teléfono", (r) => linkSel(sid, { type: "matricula", enrollmentId: r.enrollmentId }, r.name + " · " + cursoCorto(r.courseName))) : null}
					</>
				) : null}
				{p.linkType === "grupo" ? (
					<>
						<label style={{ position: "static", display: "flex", gap: 8, alignItems: "center", margin: "4px 0", fontSize: 15, whiteSpace: "normal", overflowWrap: "anywhere" }}>
							<input type="radio" checked={link?.type === "nuevo_grupo"} disabled={busy} onChange={() => linkSel(sid, { type: "nuevo_grupo" })} />
							<span>Un grupo nuevo con las personas de esta matrícula <span style={{ color: "var(--wa-apagado)" }}>(todas con «{p.name}»)</span></span>
						</label>
						<label style={{ position: "static", display: "flex", gap: 8, alignItems: "center", margin: "4px 0", fontSize: 15, whiteSpace: "normal", overflowWrap: "anywhere" }}>
							<input type="radio" checked={link?.type === "grupo" || (!!buscar && buscar.sid === sid && buscar.kind === "grupo")} disabled={busy} onChange={() => setBuscar({ sid, q: "", kind: "grupo" })} />
							<span>Unirse a un grupo que ya existe{link?.type === "grupo" && elegido("grupo") ? <b>: {elegido("grupo")}</b> : null}</span>
						</label>
						{buscar && buscar.sid === sid && buscar.kind === "grupo" ? buscador(sid, "grupo", "Busca a una persona del grupo", (r) => linkSel(sid, { type: "grupo", groupId: r.group.id, regroup: "todos" }, r.group.members.join(", "))) : null}
						{link?.type === "grupo" && grupoPrev ? (
							<div style={{ marginTop: 8 }}>
								<div style={{ fontSize: 14, fontWeight: 700 }}>¿Qué pasa con los que ya están? <span style={{ fontWeight: 400, color: "var(--wa-apagado)" }}>Lo pagado nunca se toca.</span></div>
								<label style={{ position: "static", display: "flex", gap: 8, alignItems: "flex-start", margin: "4px 0", fontSize: 14 }}>
									<input type="radio" checked={(link.regroup || "todos") === "todos"} disabled={busy} onChange={() => setItem(sid, { link: { ...link, regroup: "todos" } })} />
									<span>Todos pasan a «{p.name}» desde el {fecha(grupoPrev.desde)} <span style={{ color: "var(--wa-apagado)" }}>(recomendado)</span></span>
								</label>
								<label style={{ position: "static", display: "flex", gap: 8, alignItems: "flex-start", margin: "4px 0", fontSize: 14 }}>
									<input type="radio" checked={link.regroup === "conservar"} disabled={busy} onChange={() => setItem(sid, { link: { ...link, regroup: "conservar" } })} />
									<span>Los que ya están conservan su descuento; solo esta persona entra con «{p.name}»</span>
								</label>
								<table style={{ width: "100%", fontSize: 13, marginTop: 4 }}>
									<thead><tr style={{ color: "var(--wa-apagado)" }}><th>Persona</th><th>Hoy</th><th style={{ textAlign: "right" }}>{grupoPrev.members[0]?.month || "Mes Siguiente"}</th><th style={{ textAlign: "right" }}>Si Todos Cambian</th></tr></thead>
									<tbody>{grupoPrev.members.map((m: any, i: number) => (
										<tr key={"gm" + i} style={{ borderTop: "1px solid var(--wa-filo)" }}>
											<td>{m.name} <span style={{ color: "var(--wa-apagado)" }}>· {m.roleName}</span></td><td>{m.currentPromo}</td>
											<td style={{ textAlign: "right" }}>{m.before !== null ? sym + num(m.before) : "—"}</td>
											<td style={{ textAlign: "right", fontWeight: 700, color: m.after > m.before ? "var(--wa-oro)" : undefined }}>{m.after !== null ? sym + num(m.after) : "—"}{m.after > m.before ? " ↑" : ""}</td>
										</tr>
									))}</tbody>
								</table>
							</div>
						) : null}
					</>
				) : null}
				{p.linkType === "referido_por" ? (
					<>
						{link?.type === "alumno" && elegido("alumno") ? <div style={{ fontSize: 15 }}>Lo refirió: <b>{elegido("alumno")}</b></div> : null}
						{buscador(sid, "alumno", "Busca al alumno o graduado que lo refirió", (r) => linkSel(sid, { type: "alumno", studentId: r.id }, r.name))}
					</>
				) : null}
			</div>
		);
	};

	if (scheduleIds.length < 1) {
		return <div className="waAviso">Primero elige el curso (paso Cursos).</div>;
	}
	const hayDiezDias = (opts?.issues || []).some((i: string) => /\d+ días entre inscripciones/.test(i));   // la ventana de grupo (15 días desde 2026-10-09)

	return (
		<>
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}
			{opts ? <div style={{ fontSize: 14, color: "var(--wa-apagado)", marginBottom: 10 }}>{opts.audienceName}</div> : null}
			{(opts?.schedules || []).map((s: any) => {
				const it = itemOf(s.scheduleId);
				const elegida = it.promotionId ? (s.promotions || []).find((p: any) => p.id === it.promotionId) : null;
				const mensuales = (s.promotions || []).filter((p: any) => p.group === "mensualidad");
				const buenas = mensuales.filter((p: any) => p.eligible);
				const malas = mensuales.filter((p: any) => !p.eligible);
				// Requisito que no se cumple pero se puede pedir como excepción (ESPEC_AUDITORIA_PREGUNTAS B); las imposibles no se ofrecen.
				const conExcepcion = malas.filter((p: any) => p.exceptable);
				const exc = it.exception || null;
				const insc = (s.promotions || []).filter((p: any) => p.group === "inscripcion" && p.eligible);
				// Inscripción gratis POR DEFECTO (Esteban, 2026-10-21): sin tocar (null) va la del plantel; 0 = la quitaron (precio de lista).
				const inscId: number | null = it.inscriptionPromotionId === null || it.inscriptionPromotionId === undefined ? (s.defaultInscriptionPromotionId ?? null) : (it.inscriptionPromotionId || null);
				const inscPromo = inscId ? insc.find((p: any) => p.id === inscId) || null : null;
				const inscPrecio = conPromoInsc(Number(s.subscription) || 0, inscPromo);
				const conInsc = (strip: any[]) => (strip || []).map((m: any) => m.n === 0 && inscPromo ? { ...m, price: inscPrecio, state: inscPrecio <= 0.009 ? "gratis" : "descuento" } : m);
				const falta = marcar && it.promotionId === null;
				return (
					<div key={"pp" + s.scheduleId} className={"waTarjeta" + (falta ? " waPasoFalta" : "")}>
						<div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
							<div><b>{cursoCorto(s.courseName)}</b> <span style={{ fontSize: 14, color: "var(--wa-apagado)" }}>· {s.monthsTotal} mensualidades de {sym}{num(s.monthly)}{s.subscription > 0 ? " · inscripción " + sym + num(s.subscription) : " · inscripción gratis"}</span></div>
						</div>
						<select className="form-select" style={{ marginTop: 8 }} value={it.promotionId === null ? "" : String(it.promotionId)} disabled={busy}
							onChange={(e) => setItem(s.scheduleId, { promotionId: e.target.value === "" ? null : Number(e.target.value), link: null, exception: null })}>
							<option value="">Elige la promoción…</option>
							<option value="0">Ninguna · Precio de Lista ({sym}{num(s.noneTotal)})</option>
							{buenas.map((p: any) => <option key={"o" + p.id} value={p.id}>{p.name} · {sym}{num(p.total)}{p.linkType ? " · con otra persona" : ""}{p.window?.quota?.text ? " · " + p.window.quota.text : ""}</option>)}
							{conExcepcion.length > 0 ? (
								<optgroup label="Con Excepción (la aprueba Supervisión o Dirección de Admisiones)">
									{conExcepcion.map((p: any) => <option key={"x" + p.id} value={p.id}>{p.name} · {sym}{num(p.total)} · requiere excepción</option>)}
								</optgroup>
							) : null}
						</select>
						{falta ? <div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 3 }}>Elige la promoción (o «Ninguna») · falta</div> : null}
						{elegida && (!elegida.eligible || exc || (s.exceptableIssues || []).length > 0 || (s.waived || []).length > 0) ? (
							<div className="waTarjeta" style={{ marginTop: 8, borderColor: "var(--wa-oro)" }}>
								<div style={{ fontWeight: 700 }}>Excepción</div>
								{(s.waived || []).length > 0 ? <div style={{ fontSize: 14 }}>Se deja pasar: {(s.waived as string[]).join(" · ")}</div>
									: <div style={{ fontSize: 14, color: "var(--wa-oro)" }}>{[elegida.reason, ...((s.exceptableIssues || []) as string[])].filter((x: any, i: number, a: any[]) => x && a.indexOf(x) === i).join(" · ")}</div>}
								{exc && exc.approvedBy ? (
									<>
										<div style={{ fontSize: 14, marginTop: 4 }}>Aprobada por <b>{exc.approvedBy}</b>{exc.requestedBy && exc.requestedBy !== exc.approvedBy ? " · la pidió " + exc.requestedBy : ""} · {exc.reason}</div>
										{(s.exceptableIssues || []).length > 0 ? (
											<>
												<div style={{ fontSize: 13, color: "var(--wa-oro)" }}>Cambió algo que la excepción no cubre: hay que aprobarla otra vez.</div>
												{opts?.canApproveException ? (
													<label style={{ position: "static", display: "flex", gap: 10, alignItems: "flex-start", marginTop: 6, fontSize: 15, minHeight: 44, whiteSpace: "normal", overflow: "visible", textOverflow: "clip", maxWidth: "100%", cursor: "pointer" }}>
														<input type="checkbox" style={{ width: 22, height: 22, flex: "0 0 22px", marginTop: 1 }} checked={!!exc?.approve} disabled={busy} onChange={(e) => setItem(s.scheduleId, { exception: { ...exc, reason: exc.reason, approve: e.target.checked } })} />
														<span>Apruebo la excepción otra vez (Supervisión / Dirección de Admisiones)</span>
													</label>
												) : null}
											</>
										) : null}
									</>
								) : (
									<>
										<textarea className="form-control" rows={2} style={{ marginTop: 6, fontSize: 16 }} placeholder="Por qué se pide la excepción (al menos 10 caracteres)" value={exc?.reason || ""} disabled={busy}
											onChange={(e) => setItem(s.scheduleId, { exception: e.target.value.trim() ? { reason: e.target.value, approve: !!exc?.approve && !!opts?.canApproveException } : null })} />
										{opts?.canApproveException ? (
											<label style={{ position: "static", display: "flex", gap: 10, alignItems: "flex-start", marginTop: 6, fontSize: 15, minHeight: 44, whiteSpace: "normal", overflow: "visible", textOverflow: "clip", maxWidth: "100%", cursor: "pointer" }}>
												<input type="checkbox" style={{ width: 22, height: 22, flex: "0 0 22px", marginTop: 1 }} checked={!!exc?.approve} disabled={busy || !(exc?.reason || "").trim()}
													onChange={(e) => setItem(s.scheduleId, { exception: { reason: exc?.reason || "", approve: e.target.checked } })} />
												<span>Apruebo la excepción (Supervisión / Dirección de Admisiones)</span>
											</label>
										) : null}
									</>
								)}
							</div>
						) : null}
						{elegida && (elegida.window?.countdownToIso || elegida.window?.quota?.text) ? (
							<div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
								<CuentaRegresiva toIso={elegida.window?.countdownToIso} serverNowIso={elegida.window?.serverNowIso} onExpire={() => setTick((x) => x + 1)} />
								<CupoTexto quota={elegida.window?.quota} />
							</div>
						) : null}
						{elegida ? (
							<div style={{ marginTop: 8 }}>
								<TiraDeMeses meses={conInsc(elegida.strip)} sym={sym} />
								<div style={{ fontSize: 14 }}>Total del curso <b>{sym}{num(elegida.total)}</b> <span style={{ color: "var(--wa-apagado)" }}>en vez de {sym}{num(elegida.listTotal)}</span>{elegida.conditionText ? <span style={{ color: "var(--wa-oro)" }}> · {elegida.conditionText}</span> : null}</div>
								{elegida.conditions ? <div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{elegida.conditions}</div> : null}
								{elegida.linkType ? vinculo(s.scheduleId, elegida) : null}
							</div>
						) : it.promotionId === 0 ? <TiraDeMeses meses={conInsc(s.noneStrip)} sym={sym} compacta /> : null}
						{insc.length > 0 ? (
							<label className="waInscPromo" style={{ position: "static", display: "flex", gap: 10, alignItems: "center", marginTop: 8, fontSize: 15, minHeight: 44, whiteSpace: "normal", overflow: "visible", textOverflow: "clip", maxWidth: "100%", cursor: "pointer" }}>
								<input type="checkbox" style={{ width: 22, height: 22, flex: "0 0 22px" }} checked={!!inscPromo} disabled={busy}
									onChange={(e) => setItem(s.scheduleId, { inscriptionPromotionId: e.target.checked ? (s.defaultInscriptionPromotionId ?? insc[0].id) : 0 })} />
								<span>{(inscPromo || insc.find((p: any) => p.id === s.defaultInscriptionPromotionId) || insc[0]).name}{" "}
									<span style={{ color: "var(--wa-apagado)", whiteSpace: "nowrap" }}>{inscPromo
										? <>Inscripción <s>{sym}{num(s.subscription)}</s> → <b>{sym}{num(inscPrecio)}</b></>
										: <>Inscripción a Precio de Lista <b>{sym}{num(s.subscription)}</b></>}</span></span>
							</label>
						) : null}
						{malas.length > 0 ? (
							<details style={{ marginTop: 6, fontSize: 13, color: "var(--wa-apagado)" }}>
								<summary>No Aplican a Este Curso ({malas.length})</summary>
								{malas.map((p: any) => <div key={"x" + p.id}>{p.reason}</div>)}
							</details>
						) : null}
					</div>
				);
			})}
			{loading && !opts ? <div className="loader" /> : null}

			{opts?.referral ? (
				<div className="waTarjeta">
					<div style={{ fontWeight: 700 }}>¿Lo refirió un alumno actual?</div>
					{value.referredBy ? (
						<div style={{ fontSize: 15, marginTop: 4 }}>Lo refirió: <b>{elegidos["ref"] || "matrícula #" + value.referredBy.enrollmentId}</b> · <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => onChange({ ...value, referredBy: null })}>Quitar</button></div>
					) : buscador("ref", "matricula", "Busca al alumno que lo refirió", (r) => { setElegidos((e) => ({ ...e, ref: r.name + " · " + cursoCorto(r.courseName) })); onChange({ ...value, referredBy: { enrollmentId: r.enrollmentId } }); setBuscar(null); setResultados(null); })}
				</div>
			) : null}

			{directa ? null : <div className="waTarjeta">
				<div style={{ fontWeight: 700 }}>Personas en Esta Matrícula</div>
				<div style={{ fontSize: 15, marginTop: 4 }}><FontAwesomeIcon icon={solid("user")} />&nbsp;<b>{studentName || "Esta persona"}</b> <span style={{ color: "var(--wa-apagado)" }}>(esta)</span></div>
				{bundle.map((b) => (
					<div key={"b" + b.id} style={{ fontSize: 15, marginTop: 4 }}>
						<FontAwesomeIcon icon={solid("user")} />&nbsp;<b>{b.name}</b> <span style={{ color: "var(--wa-apagado)" }}>· #{b.id} · {b.status}{b.schedules.length ? " · " + b.schedules.map((x) => cursoCorto(x)).join(", ") : ""}</span>
						{onOpenRequest ? <> · <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => onOpenRequest(b.id)}>Abrir</button></> : null}
					</div>
				))}
				<div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
					{onAddPerson ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={onAddPerson}><FontAwesomeIcon icon={solid("user-plus")} />&nbsp;Agregar Otra Persona a Esta Matrícula</button> : null}
					{bundle.length > 0 && onLeaveBundle ? <button type="button" className="btn btn-link btn-sm waOroTexto" disabled={busy} onClick={onLeaveBundle}>Sacar a Esta Persona del Grupo</button> : null}
				</div>
			</div>}

			{hayDiezDias && opts?.canAuthorize10 ? (
				<div className="waTarjeta" style={{ borderColor: "var(--wa-oro)" }}>
					<div style={{ fontWeight: 700 }}>Autorizar Fuera de los {opts.diasGrupo} Días (Dirección de Ventas)</div>
					<input className="form-control" style={{ marginTop: 6 }} placeholder="Por qué se autoriza (queda en el historial)" value={value.override10?.reason || ""} disabled={busy}
						onChange={(e) => onChange({ ...value, override10: e.target.value.trim() ? { reason: e.target.value } : null })} />
				</div>
			) : null}

			{(opts?.issues || []).length > 0 ? (
				<div className="waFaltaAviso">
					{(opts.issues as string[]).map((a, i) => <div key={"pi" + i}><FontAwesomeIcon icon={solid("circle-exclamation")} />&nbsp;{a.replace(/^Promoción( \([^)]*\))?: /, "")}</div>)}
				</div>
			) : null}
			{(opts?.bundleIssues || []).length > 0 ? (
				<div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 6 }}>En las otras personas: {(opts.bundleIssues as string[]).join(" · ")}</div>
			) : null}

		</>
	);
};

export default PasoPromocion;
